import { ChunkBoundary, ChunkFallback } from '@/components/ap/skeletons';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SidebarMenuButton, SidebarMenuItem } from '@/components/ui/sidebar';
import { useCurrentUser, useEnabledFeatures } from '@/hooks/dataHelpers/useCurrentUser';
import { useCurrentExperience } from '@/hooks/useCurrentExperience';
import { lazyWithRetry } from '@/lib/lazyWithRetry';
import { getAppType } from '@/lib/offline/OfflineAppHelper';
import {
  ArrowRightUpLinear,
  BellLinear,
  BookLinear,
  CalendarAddLinear,
  ChatRoundDotsLinear,
  QuestionCircleLinear,
} from '@assessprep/icons/solar';
import { Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';

// Loaded on "Report an issue" only — FeedbackDialog pulls APRichTextEditor
// (the whole tiptap family), which must never ride the entry chunk.
const FeedbackDialog = lazyWithRetry(
  () => import('@/components/ap/feedback'),
  (m) => ({ default: m.FeedbackDialog })
);

/**
 * Sidebar-footer support cluster (v3 SiderFooter parity, moved out of
 * UserMenu per dev direction 03 Aug): "What's new" + "Help center" render as
 * their own footer items above the user pill, admin/teacher only — students
 * keep their APL-12040 nav resource (external For-Students Zendesk link)
 * instead, so no role ever sees two Help entries. Help center opens the v3
 * APSupportMenu options restyled on the v4 kit: Help Guides (Zendesk),
 * Book Training Call (admin-only, Calendly), Report an issue (FeedbackDialog).
 */

const HELP_GUIDES_URL = 'https://assessprep.zendesk.com/hc/en-us';
const BOOK_TRAINING_URL = 'https://calendly.com/assessprepdemo/assessprep-training60min?back=1';

const openExternal = (url: string) => window.open(url, '_blank', 'noopener,noreferrer');

const OptionMeta = ({ title, subtitle }: { title: string; subtitle: string }) => (
  <span className="flex min-w-0 flex-1 flex-col">
    <span className="truncate text-sm">{title}</span>
    <span className="truncate text-xs text-muted-foreground">{subtitle}</span>
  </span>
);

const ExternalMark = () => (
  <ArrowRightUpLinear aria-hidden className="ms-auto size-3.5 shrink-0 text-muted-foreground" />
);

export const SidebarSupport = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const currentUser = useCurrentUser();
  const enabledFeatures = useEnabledFeatures();
  // v3 FeedbackModal parity — an open experience lands in feedback's
  // experienceInfo even when reported from chrome.
  const experience = useCurrentExperience();
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  // v3 SiderFooter gate: the whole support cluster is admin/teacher-only.
  // Web-only (dev direction): Help center + What's new never render inside the
  // lockdown apps' webviews — same getAppType() === 'web' idiom as the
  // UserMenu profile gate. The user pill itself stays everywhere.
  const isAdminOrTeacher = currentUser?.role === 'admin' || currentUser?.role === 'teacher';
  if (!isAdminOrTeacher || getAppType() !== 'web') return null;

  const showWhatsNew = !!enabledFeatures?.enable_whats_new;
  const showBookTraining = currentUser?.role === 'admin';
  const isWhatsNewActive = pathname === '/whats-new';

  return (
    <>
      {feedbackOpen && (
        <ChunkBoundary variant="scrim" onDismiss={() => setFeedbackOpen(false)}>
          <Suspense
            fallback={<ChunkFallback variant="scrim" onDismiss={() => setFeedbackOpen(false)} />}
          >
            <FeedbackDialog
              open={feedbackOpen}
              onOpenChange={setFeedbackOpen}
              experience={experience}
            />
          </Suspense>
        </ChunkBoundary>
      )}
      {showWhatsNew && (
        <SidebarMenuItem>
          <SidebarMenuButton
            asChild
            isActive={isWhatsNewActive}
            tooltip={t('Announcements.whats_new')}
          >
            <Link
              to="/whats-new"
              aria-current={isWhatsNewActive ? 'page' : undefined}
              data-testid="sidebar-whats-new"
            >
              <BellLinear aria-hidden />
              <span>{t('Announcements.whats_new')}</span>
            </Link>
          </SidebarMenuButton>
        </SidebarMenuItem>
      )}
      <SidebarMenuItem>
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              tooltip={t('APSupportMenu.help_center')}
              data-testid="sidebar-help-center"
            >
              <QuestionCircleLinear aria-hidden />
              <span>{t('APSupportMenu.help_center')}</span>
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="start" className="w-72">
            <DropdownMenuItem
              onSelect={() => openExternal(HELP_GUIDES_URL)}
              data-testid="sidebar-help-option-help-guides"
            >
              <BookLinear aria-hidden className="size-4 shrink-0 text-muted-foreground" />
              <OptionMeta
                title={t('APSupportMenu.help_guides')}
                subtitle={t('APSupportMenu.browse_documentation_and_tutorials')}
              />
              <ExternalMark />
            </DropdownMenuItem>
            {showBookTraining && (
              <DropdownMenuItem
                onSelect={() => openExternal(BOOK_TRAINING_URL)}
                data-testid="sidebar-help-option-book-training"
              >
                <CalendarAddLinear aria-hidden className="size-4 shrink-0 text-muted-foreground" />
                <OptionMeta
                  title={t('APSupportMenu.book_training_call')}
                  subtitle={t('APSupportMenu.schedule_a_session_with_our_team')}
                />
                <ExternalMark />
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onSelect={() => setFeedbackOpen(true)}
              data-testid="sidebar-help-option-report-issue"
            >
              <ChatRoundDotsLinear aria-hidden className="size-4 shrink-0 text-muted-foreground" />
              <OptionMeta
                title={t('CommonText.report_an_issue')}
                subtitle={t('APSupportMenu.submit_an_issue_or_request')}
              />
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </>
  );
};
